import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Phone,
    Mail,
    MapPin,
    Navigation,
    CreditCard,
    ShoppingBag,
    Calendar,
    Building,
    Users,
    IndianRupee,
    LocateFixed,
    ClipboardList,
    UserPlus,
    KeyRound,
    Smartphone
} from 'lucide-react';
import {
    getPartyById,
    getPartyUsers,
    getRecentOrdersForParty,
    updatePartyLocation
} from '@/app/services/partyService';
import {
    getCurrentCoordinates,
    buildMapsUrl,
    formatCoordinates,
    geoLocationToMapsUrl,
    type Coordinates
} from '@/app/lib/geolocation';
import { canCreateSalesOrders, canManageClientAccess } from '@/app/lib/permissions';
import type { PartyUser } from '@/app/types/party';
import { useAuthStore } from '@/app/store/authStore';
import { ClientAccessSheet } from './components/ClientAccessSheet';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { MobileCardGroup, MobileCardRow } from '@/components/ui/MobileCard';
import { MobileBadge } from '@/components/ui/MobileBadge';
import { MobileSkeleton } from '@/components/ui/MobileSkeleton';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';

export default function PartyDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);

    // Get user's site IDs for querying vouchers across all accessible sites
    const userSiteIds = user?.sites?.map(s => s.id) || [];

    // Sales-order creation is restricted to org_admin / data_entry on the backend.
    const canCreateOrders = canCreateSalesOrders(user);

    // Creating customer app logins and resetting PINs is org_admin-only.
    const canManageAccess = canManageClientAccess(user);

    // Location capture flow state
    const [locating, setLocating] = useState(false);
    const [captured, setCaptured] = useState<Coordinates | null>(null);
    const [confirmReplace, setConfirmReplace] = useState(false);

    // Client-access sheet. Mode and target user are kept separate from `open` so
    // the sheet stays mounted through its close animation.
    const [accessMode, setAccessMode] = useState<'create' | 'reset'>('create');
    const [accessUser, setAccessUser] = useState<PartyUser | null>(null);
    const [accessOpen, setAccessOpen] = useState(false);

    const { data: party, isLoading: partyLoading } = useQuery({
        queryKey: ['party', id],
        queryFn: () => getPartyById(Number(id)),
        enabled: !!id,
    });

    // Drives create-vs-reset: the party payload carries no client-user field, so
    // the linked user list is the only way to know whether a login already exists.
    const { data: partyUsers, isLoading: partyUsersLoading } = useQuery({
        queryKey: ['party-users', Number(id)],
        queryFn: () => getPartyUsers(Number(id)),
        enabled: !!id && canManageAccess,
    });

    // Parties can also be linked to field_agent and org_admin users; only a
    // `client` account is customer app access. A party can have several — each
    // gets its own row, so a reset always names the user it will affect.
    const clientUsers = partyUsers?.filter((u) => u.role === 'client') ?? [];

    const locationMutation = useMutation({
        mutationFn: (geoLocation: string) =>
            updatePartyLocation(Number(id), geoLocation),
        onSuccess: () => {
            toast.success('Location saved');
            queryClient.invalidateQueries({ queryKey: ['party', id] });
            setCaptured(null);
            setConfirmReplace(false);
        },
        onError: (err: unknown) => {
            let message = 'Failed to save location';
            if (axios.isAxiosError(err)) {
                message = err.response?.data?.detail || err.message || message;
            } else if (err instanceof Error) {
                message = err.message;
            }
            toast.error(message);
        },
    });

    const { data: recentOrders = [], isLoading: orderLoading } = useQuery({
        queryKey: ['party-recent-orders', id, userSiteIds],
        queryFn: () => getRecentOrdersForParty(Number(id), userSiteIds, 5),
        enabled: !!id && userSiteIds.length > 0,
    });

    if (partyLoading) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Loading..." showBack />
                <div className="p-4 space-y-4">
                    <MobileSkeleton className="h-32 w-full rounded-xl" />
                    <MobileSkeleton className="h-48 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    if (!party) {
        return (
            <div className="flex flex-col min-h-full">
                <MobileHeader title="Not Found" showBack />
                <div className="flex-1 flex items-center justify-center p-4">
                    <p className="text-muted-foreground">Party not found</p>
                </div>
            </div>
        );
    }

    const creditLimitDisplay = party.credit_limit_type === 'no_limit'
        ? 'No Limit'
        : `₹${parseFloat(party.credit_limit_amount || '0').toLocaleString()}`;

    const handleOpenMaps = () => {
        if (party.geo_location) {
            window.open(geoLocationToMapsUrl(party.geo_location), '_blank');
        }
    };

    const handleCaptureLocation = async () => {
        setLocating(true);
        try {
            const coords = await getCurrentCoordinates();
            setCaptured(coords);
            setConfirmReplace(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to get location');
        } finally {
            setLocating(false);
        }
    };

    const handleVerifyCaptured = () => {
        if (captured) {
            window.open(buildMapsUrl(captured.lat, captured.lng), '_blank');
        }
    };

    const handleSaveCaptured = () => {
        if (!captured) return;
        // Confirm before overwriting an existing location
        if (party.geo_location && !confirmReplace) {
            setConfirmReplace(true);
            return;
        }
        locationMutation.mutate(formatCoordinates(captured.lat, captured.lng));
    };

    const closeSheet = () => {
        if (locationMutation.isPending) return;
        setCaptured(null);
        setConfirmReplace(false);
    };

    const handleCall = () => {
        if (party.phone) {
            window.location.href = `tel:${party.phone}`;
        }
    };

    const handleEmail = () => {
        if (party.email) {
            window.location.href = `mailto:${party.email}`;
        }
    };

    return (
        <div className="flex flex-col min-h-full">
            <MobileHeader title="Party Details" showBack />

            <div className="flex-1 mobile-scroll-area pb-tab-bar">
                <div className="p-4 space-y-6">
                    {/* Header Card */}
                    <div className="mobile-card flex flex-col items-center py-6">
                        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                            <span className="text-2xl font-bold text-primary">
                                {party.name.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <h2 className="text-xl font-bold text-foreground mb-1 text-center">
                            {party.name}
                        </h2>
                        <MobileBadge variant="secondary">
                            {party.primary_party_type}
                        </MobileBadge>
                    </div>

                    {/* Actions */}
                    <div>
                        <h2 className="mobile-section-header">Actions</h2>
                        <MobileCardGroup>
                            {canCreateOrders && (
                                <MobileCardRow
                                    onClick={() =>
                                        navigate(`/sales-orders/new?party_id=${party.id}`)
                                    }
                                    showArrow
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <ClipboardList className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-foreground">
                                                New Sales Order
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Take the order now, admin invoices it
                                            </p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            )}
                            <MobileCardRow
                                onClick={() =>
                                    navigate(`/payments-in/new?party_id=${party.id}`)
                                }
                                showArrow
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <IndianRupee className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-foreground">
                                            Record Payment
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Receive cash, UPI, or bank transfer
                                        </p>
                                    </div>
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>

                    {/* Customer app access — create the login or reset the PIN on
                        the spot, instead of phoning the office to do it. */}
                    {canManageAccess && (
                        <div>
                            <h2 className="mobile-section-header">App Access</h2>
                            <MobileCardGroup>
                                {partyUsersLoading ? (
                                    <MobileCardRow>
                                        <div className="flex items-center gap-3">
                                            <MobileSkeleton className="w-10 h-10 rounded-full" />
                                            <div className="space-y-2 flex-1">
                                                <MobileSkeleton className="h-4 w-32" />
                                                <MobileSkeleton className="h-3 w-40" />
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                ) : clientUsers.length > 0 ? (
                                    clientUsers.map((u) => (
                                        <MobileCardRow
                                            key={u.id}
                                            onClick={() => {
                                                setAccessMode('reset');
                                                setAccessUser(u);
                                                setAccessOpen(true);
                                            }}
                                            showArrow
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                                                    <KeyRound className="h-5 w-5 text-amber-500" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-semibold text-foreground truncate">
                                                        Reset PIN · {u.username}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {clientUsers.length > 1
                                                            ? 'Only this login is affected'
                                                            : 'Customer app login'}
                                                        {!u.is_active && ' · account disabled'}
                                                    </p>
                                                </div>
                                            </div>
                                        </MobileCardRow>
                                    ))
                                ) : party.phone ? (
                                    <MobileCardRow
                                        onClick={() => {
                                            setAccessMode('create');
                                            setAccessUser(null);
                                            setAccessOpen(true);
                                        }}
                                        showArrow
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                <UserPlus className="h-5 w-5 text-primary" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-foreground">
                                                    Create App Login
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    Signs in with {party.phone}
                                                </p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                ) : (
                                    <MobileCardRow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                                <Smartphone className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-medium text-muted-foreground">
                                                    No phone number
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    The phone number becomes the user ID — ask
                                                    the office to add one first
                                                </p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Contact Info */}
                    {(party.phone || party.email) && (
                        <div>
                            <h2 className="mobile-section-header">Contact</h2>
                            <MobileCardGroup>
                                {party.phone && (
                                    <MobileCardRow onClick={handleCall} showArrow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                                                <Phone className="h-5 w-5 text-green-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Phone</p>
                                                <p className="font-medium">{party.phone}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.email && (
                                    <MobileCardRow onClick={handleEmail} showArrow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                                <Mail className="h-5 w-5 text-blue-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Email</p>
                                                <p className="font-medium">{party.email}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Addresses */}
                    <div>
                            <h2 className="mobile-section-header">Addresses</h2>
                            <MobileCardGroup>
                                {party.billing_address && (
                                    <MobileCardRow>
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                                <MapPin className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Billing Address</p>
                                                <p className="font-medium whitespace-pre-line">{party.billing_address}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.shipping_address && party.has_separate_shipping && (
                                    <MobileCardRow>
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                                <MapPin className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Shipping Address</p>
                                                <p className="font-medium whitespace-pre-line">{party.shipping_address}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.geo_location && (
                                    <MobileCardRow onClick={handleOpenMaps} showArrow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                                                <Navigation className="h-5 w-5 text-red-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Location</p>
                                                <p className="font-medium text-primary">Open in Maps</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                <MobileCardRow
                                    onClick={locating ? undefined : handleCaptureLocation}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            {locating ? (
                                                <LocateFixed className="h-5 w-5 text-primary animate-pulse" />
                                            ) : (
                                                <LocateFixed className="h-5 w-5 text-primary" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs text-muted-foreground">
                                                GPS
                                            </p>
                                            <p className="font-medium text-primary">
                                                {locating
                                                    ? 'Getting your location…'
                                                    : party.geo_location
                                                        ? 'Update location'
                                                        : 'Capture current location'}
                                            </p>
                                        </div>
                                    </div>
                                </MobileCardRow>
                            </MobileCardGroup>
                        </div>

                    {/* Recent Orders */}
                    <div>
                        <h2 className="mobile-section-header">Recent Orders</h2>
                        <MobileCardGroup>
                            {orderLoading ? (
                                <>
                                    {[1, 2, 3].map((i) => (
                                        <MobileCardRow key={i}>
                                            <div className="flex items-center gap-3">
                                                <MobileSkeleton className="w-10 h-10 rounded-full" />
                                                <div className="space-y-2 flex-1">
                                                    <MobileSkeleton className="h-4 w-24" />
                                                    <MobileSkeleton className="h-4 w-32" />
                                                </div>
                                            </div>
                                        </MobileCardRow>
                                    ))}
                                </>
                            ) : recentOrders.length > 0 ? (
                                <>
                                    {recentOrders.map((order) => {
                                        const isPaid = order.payment_status === 'Paid';
                                        const isPartial = order.payment_status === 'Partially Paid';
                                        return (
                                        <MobileCardRow key={order.id}>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <ShoppingBag className="h-5 w-5 text-primary" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-sm font-medium truncate">
                                                            #{order.voucher_number}
                                                        </p>
                                                        <p className="font-semibold text-primary whitespace-nowrap">
                                                            ₹{parseFloat(order.total_amount).toLocaleString()}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-0.5">
                                                        <p className="text-xs text-muted-foreground">
                                                            {format(new Date(order.transaction_date), 'PPP')}
                                                        </p>
                                                        {order.payment_status && (
                                                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                                                isPaid
                                                                    ? 'bg-green-500/10 text-green-500'
                                                                    : isPartial
                                                                        ? 'bg-yellow-500/10 text-yellow-500'
                                                                        : 'bg-red-500/10 text-red-500'
                                                            }`}>
                                                                {isPartial && order.amount_due
                                                                    ? `Due ₹${parseFloat(order.amount_due).toLocaleString()}`
                                                                    : order.payment_status}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </MobileCardRow>
                                        );
                                    })}
                                </>
                            ) : (
                                <MobileCardRow>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                            <ShoppingBag className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                        <p className="text-muted-foreground">No orders yet</p>
                                    </div>
                                </MobileCardRow>
                            )}
                        </MobileCardGroup>
                    </div>

                    {/* Credit Info */}
                    <div>
                        <h2 className="mobile-section-header">Credit Information</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                                        <CreditCard className="h-5 w-5 text-yellow-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Credit Limit</p>
                                        <p className="font-medium">{creditLimitDisplay}</p>
                                    </div>
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>

                    {/* Additional Info */}
                    {(party.party_group || party.site) && (
                        <div>
                            <h2 className="mobile-section-header">Additional Info</h2>
                            <MobileCardGroup>
                                {party.party_group && (
                                    <MobileCardRow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                                <Users className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Party Group</p>
                                                <p className="font-medium">{party.party_group.name}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}

                                {party.site && (
                                    <MobileCardRow>
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                                <Building className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">Site</p>
                                                <p className="font-medium">{party.site.name}</p>
                                            </div>
                                        </div>
                                    </MobileCardRow>
                                )}
                            </MobileCardGroup>
                        </div>
                    )}

                    {/* Record */}
                    <div>
                        <h2 className="mobile-section-header">Record</h2>
                        <MobileCardGroup>
                            <MobileCardRow>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                                        <Calendar className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-muted-foreground">Created</p>
                                        <p className="font-medium">
                                            {format(new Date(party.created_at), 'MMMM d, yyyy')}
                                        </p>
                                    </div>
                                </div>
                            </MobileCardRow>
                        </MobileCardGroup>
                    </div>
                </div>
            </div>

            <MobileBottomSheet
                open={!!captured}
                onClose={closeSheet}
                title={confirmReplace ? 'Replace location?' : 'Confirm location'}
                heightMode="auto"
                showCloseButton={!locationMutation.isPending}
            >
                {captured && (
                    <div className="p-4 space-y-5">
                        {confirmReplace ? (
                            <p className="text-sm text-muted-foreground">
                                This party already has a saved location. Replace it
                                with the location you just captured?
                            </p>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Make sure you're standing at the party's location, then
                                save.
                            </p>
                        )}

                        <div className="rounded-xl bg-secondary p-4 space-y-2">
                            <div className="flex items-center gap-3">
                                <Navigation className="h-5 w-5 text-primary flex-shrink-0" />
                                <div>
                                    <p className="text-xs text-muted-foreground">
                                        Coordinates
                                    </p>
                                    <p className="font-medium tabular-nums">
                                        {captured.lat.toFixed(6)}, {captured.lng.toFixed(6)}
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground pl-8">
                                Accuracy ±{Math.round(captured.accuracy)} m
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={handleVerifyCaptured}
                            className="flex items-center gap-2 text-sm font-medium text-primary"
                        >
                            <MapPin className="h-4 w-4" />
                            Verify in Maps
                        </button>

                        <div className="space-y-3 pt-2">
                            <MobileButton
                                type="button"
                                size="lg"
                                className="w-full"
                                isLoading={locationMutation.isPending}
                                onClick={handleSaveCaptured}
                            >
                                {confirmReplace ? 'Replace location' : 'Save location'}
                            </MobileButton>
                            <MobileButton
                                type="button"
                                variant="secondary"
                                className="w-full"
                                disabled={locationMutation.isPending}
                                onClick={handleCaptureLocation}
                            >
                                Retry
                            </MobileButton>
                        </div>
                    </div>
                )}
            </MobileBottomSheet>

            {canManageAccess && (
                <ClientAccessSheet
                    party={party}
                    clientUser={accessUser}
                    mode={accessMode}
                    open={accessOpen}
                    onClose={() => setAccessOpen(false)}
                />
            )}
        </div>
    );
}
